import {
    API_BASE_URL,
    API_ENDPOINTS,
    getAuthHeaders,
    getAuthToken,
    setAuthToken,
    getRefreshToken,
    setRefreshToken,
} from './api-config';

interface RequestOptions extends RequestInit {
    params?: Record<string, string | number | boolean>;
}

class ApiService {
    private baseURL: string;
    private _refreshPromise: Promise<string> | null = null;

    constructor(baseURL: string) {
        this.baseURL = baseURL;
    }

    private buildURL(endpoint: string, params?: Record<string, any>): string {
        const url = new URL(`${this.baseURL}${endpoint}`);
        if (params) {
            Object.entries(params).forEach(([key, value]) => {
                if (value === undefined || value === null) return;
                url.searchParams.append(key, String(value));
            });
        }
        return url.toString();
    }

    private async doRefresh(): Promise<string> {
        const refreshToken = getRefreshToken();
        if (!refreshToken) throw new Error('No refresh token');

        const url = this.buildURL(API_ENDPOINTS.AUTH_REFRESH);
        const response = await fetch(url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ refresh_token: refreshToken }),
        });

        if (!response.ok) throw new Error('Refresh failed');

        const data = await response.json();
        const tokens = data.data ?? data;
        setAuthToken(tokens.access_token);
        setRefreshToken(tokens.refresh_token);
        return tokens.access_token;
    }

    private async refreshAccessToken(): Promise<string> {
        if (!this._refreshPromise) {
            this._refreshPromise = this.doRefresh().finally(() => {
                this._refreshPromise = null;
            });
        }
        return this._refreshPromise;
    }

    private async request<T>(endpoint: string, options: RequestOptions = {}, isRetry = false): Promise<T> {
        const { params, ...fetchOptions } = options;
        const url = this.buildURL(endpoint, params);

        const authHeaders = getAuthHeaders();
        const headers: Record<string, string> = {
            'Content-Type': 'application/json',
            ...(options.headers as Record<string, string>),
        };

        if (authHeaders.Authorization) {
            headers.Authorization = authHeaders.Authorization;
        }
        // options.headers peut surcharger X-Agency-Id (ex: création produit multi-agences)
        if (!headers['X-Agency-Id'] && authHeaders['X-Agency-Id']) {
            headers['X-Agency-Id'] = authHeaders['X-Agency-Id'];
        }

        const response = await fetch(url, { ...fetchOptions, headers });

        if (response.status === 401 && !isRetry && endpoint !== API_ENDPOINTS.AUTH_REFRESH) {
            try {
                const newToken = await this.refreshAccessToken();
                const retryHeaders = { ...headers, Authorization: `Bearer ${newToken}` };
                return this.request<T>(endpoint, { ...options, headers: retryHeaders }, true);
            } catch {
                if (typeof window !== 'undefined') {
                    window.dispatchEvent(new CustomEvent('auth:logout'));
                }
                const error: any = new Error('Session expirée. Veuillez vous reconnecter.');
                error.status = 401;
                throw error;
            }
        }

        const contentType = response.headers.get('content-type');
        const hasJson = contentType && contentType.includes('application/json');

        let data: any;
        if (hasJson) {
            try {
                data = await response.json();
            } catch {
                data = { message: 'Erreur de parsing JSON' };
            }
        } else {
            data = { message: (await response.text()) || 'Erreur serveur' };
        }

        if (!response.ok) {
            const error: any = new Error(data.message || data.error || 'Une erreur est survenue');
            error.status = response.status;
            error.statusCode = response.status;
            throw error;
        }

        return data.pagination || data.query ? data : (data.data || data);
    }

    async get<T>(endpoint: string, params?: Record<string, string | number | boolean>): Promise<T> {
        return this.request<T>(endpoint, { method: 'GET', params });
    }

    async post<T>(endpoint: string, body?: unknown, headers?: Record<string, string>): Promise<T> {
        return this.request<T>(endpoint, { method: 'POST', body: JSON.stringify(body), headers });
    }

    async put<T>(endpoint: string, body?: unknown, headers?: Record<string, string>): Promise<T> {
        return this.request<T>(endpoint, { method: 'PUT', body: JSON.stringify(body), headers });
    }

    async patch<T>(endpoint: string, body?: unknown, headers?: Record<string, string>): Promise<T> {
        return this.request<T>(endpoint, { method: 'PATCH', body: JSON.stringify(body), headers });
    }

    async delete<T>(endpoint: string, headers?: Record<string, string>): Promise<T> {
        return this.request<T>(endpoint, { method: 'DELETE', headers });
    }
}

export const apiService = new ApiService(API_BASE_URL);
